import React, { useState } from "react";
import { Card, Typography, Box, Menu, MenuItem } from "@mui/material";
import "./SenderMessage.css";
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const SenderMessage = ({ message, deleteParticularMessage, handleDownload, formatMessageTime }) => {
  // Use the formatMessageTime prop if provided, otherwise format locally
  const formattedTime = formatMessageTime 
    ? formatMessageTime(message.createdAt)
    : new Date(message.createdAt).toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
  
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderMessageContent = () => {
    // Common card styles for all message types
    const cardStyles = {
      bgcolor: '#F2F1EB',
      p: 1.5,
      borderRadius: '18px 4px 18px 18px',
      position: 'relative',
      minWidth: '60px',
      maxWidth: '300px', 
      width: 'fit-content'
    };

    if (message.contentType === "file") {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ position: 'relative' }}>
            <Card sx={cardStyles}>
              {/* Options menu */}
              <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}>
                <MoreHorizIcon
                  onClick={handleClick}
                  sx={{ fontSize: '18px', cursor: 'pointer', color: '#666' }}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      width: '120px',
                    },
                  }}
                >
                  <MenuItem onClick={deleteParticularMessage} sx={{ fontSize: '14px' }}>
                    Delete
                  </MenuItem>
                </Menu>
              </Box>

              {/* File content - Make file name container consistent width */}
              <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems="center" mb={0.5}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 500, 
                  pr: 3,
                  wordBreak: 'break-word',
                  minWidth: '120px' // Ensure file messages have minimum width
                }}>
                  {message.fileName}
                </Typography>
                {
                  message?.download ?
                    <CheckCircleIcon sx={{ fontSize: '18px', color: 'green', flexShrink: 0 }} />
                    :
                    <DownloadForOfflineIcon
                      sx={{ fontSize: '18px', cursor: 'pointer', color: '#666', flexShrink: 0 }}
                      onClick={handleDownload}
                    />
                }
              </Box>

              {/* Time */}
              <Box display='flex' justifyContent='flex-end' mt={0.5}>
                <Typography variant="caption" sx={{ 
                  color: '#666', 
                  fontSize: '11px',
                  fontWeight: 500
                }}>
                  {formattedTime}
                </Typography>
              </Box>
            </Card>
          </Box>
        </Box>
      );
    } else if (message.contentType === "audio") {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ position: 'relative' }}>
            <Card sx={{
              ...cardStyles,
              minWidth: '220px', 
              maxWidth: '280px', 
              pr: 4,
              position: 'relative'
            }}>
              
              <Box sx={{ 
                position: 'absolute', 
                top: 4, 
                right: 4, 
                zIndex: 2, 
                backgroundColor: '#F2F1EB',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MoreHorizIcon
                  onClick={handleClick}
                  sx={{ 
                    fontSize: '18px', 
                    cursor: 'pointer', 
                    color: '#666',
                    '&:hover': {
                      color: '#333'
                    }
                  }}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      width: '120px',
                      marginTop: '30px' 
                    },
                  }}
                >
                  <MenuItem onClick={deleteParticularMessage} sx={{ fontSize: '14px' }}>
                    Delete
                  </MenuItem>
                </Menu>
              </Box>

              {/* Audio content - Adjusted to not overlap with three dots */}
              <Box mb={0.5} sx={{ width: 'calc(100% - 20px)' }}> 
                <audio 
                  controls 
                  style={{ 
                    width: '100%', 
                    height: '36px',
                    minWidth: '180px' 
                  }}
                >
                  <source src={message.content} type="audio/mpeg" />
                  Your browser does not support the audio element.
                </audio>
              </Box>

              {/* Time */}
              <Box display='flex' justifyContent='flex-end' mt={0.5}>
                <Typography variant="caption" sx={{ 
                  color: '#666', 
                  fontSize: '11px',
                  fontWeight: 500
                }}>
                  {formattedTime}
                </Typography>
              </Box>
            </Card>
          </Box>
        </Box>
      );
    } else {

      return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ position: 'relative' }}>
            <Card sx={{
              ...cardStyles,
              minWidth: '80px' 
            }}>
             
              <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 1 }}>
                <MoreHorizIcon
                  onClick={handleClick}
                  sx={{ fontSize: '18px', cursor: 'pointer', color: '#666' }}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  PaperProps={{
                    style: {
                      width: '120px',
                    },
                  }}
                >
                  <MenuItem onClick={deleteParticularMessage} sx={{ fontSize: '14px' }}>
                    Delete
                  </MenuItem>
                </Menu>
              </Box>

              {/* Text content with consistent padding */}
              <Typography variant="body1" sx={{ 
                wordBreak: 'break-word',
                pr: 3,
                display: 'inline-block',
                minWidth: '40px', 
                paddingRight: '20px' 
              }}>
                {message.content}
              </Typography>

              {/* Time - Positioned at bottom right */}
              <Box display='flex' justifyContent='flex-end' mt={0.5}>
                <Typography variant="caption" sx={{ 
                  color: '#666', 
                  fontSize: '11px',
                  fontWeight: 500
                }}>
                  {formattedTime}
                </Typography>
              </Box>
            </Card>
          </Box>
        </Box>
      );
    }
  };

  return renderMessageContent();
};

export default SenderMessage;